'use client'

import type {
  AccordionItemProps,
  AccordionItemRefAttributes,
} from '@pearlconvert/ui/components/accordion/accordion.types'

import classNames from 'classnames'
import React, { forwardRef } from 'react'

import { AccordionModal } from '@pearlconvert/ui/components/accordion'
import { useAccordionItem } from '@pearlconvert/ui/components/accordion/batons/use-accordion-item'
import Heading from '@pearlconvert/ui/components/typography/heading'

const AccordionItem = forwardRef<
  AccordionItemRefAttributes,
  AccordionItemProps
>(
  (
    {
      children,
      title,
      displayAsDialog = false,
      titleTag,
      headingAttributes,
      itemId,
      disabled = false,
      footerContent,
      hash,
      slideInTitle = title,
      location,
      trackingTitle,
    }: AccordionItemProps,
    ref
  ) => {
    const {
      id,
      isExpanded,
      elementId,
      handleClose,
      handleToggle,
      headingButtonRef,
    } = useAccordionItem({
      title,
      itemId,
      hash,
      displayAsDialog,
      ref,
      location,
      trackingTitle,
    })

    if (disabled) {
      return (
        <li className={`accordion__item accordion__item--disabled`}>
          <div
            className="accordion__item-title"
            id={`accordion_${elementId}_id`}
          >
            {title}
          </div>
        </li>
      )
    }

    return (
      <li
        className={classNames('accordion__item', {
          'accordion__item--active': isExpanded,
        })}
      >
        {displayAsDialog && (
          <AccordionModal
            onClose={handleClose}
            title={slideInTitle}
            hash={hash}
            id={id.current}
            footerContent={footerContent}
          >
            {children}
          </AccordionModal>
        )}
        <Heading
          as={titleTag}
          className="accordion__item-title"
          {...headingAttributes}
        >
          <button
            className="accordion__item-trigger"
            onClick={handleToggle}
            aria-expanded={isExpanded}
            id={`accordion_${elementId}_id`}
            ref={headingButtonRef}
            aria-controls={`accordion_${elementId}_body`}
          >
            {title}
          </button>
        </Heading>

        {!displayAsDialog && (
          <div
            className="accordion__item-body"
            role="region"
            id={`accordion_${elementId}_body`}
            aria-labelledby={`accordion_${elementId}_id`}
          >
            <div className="accordion__item-body-animation-wrapper">
              <div className="accordion__item-content">{children}</div>
            </div>
          </div>
        )}
      </li>
    )
  }
)

AccordionItem.displayName = 'AccordionItem'

export default AccordionItem
